import { SORT_ICON_NONE } from '@appViews/shared/sortIcon';
import { languageLabel, testTitle } from '@boViews/shared/testDisplay';
import TestStatusBadge from '@boViews/shared/TestStatusBadge';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import Action from '@sharedComponents/Action';
import Button from '@sharedComponents/Button';
import Typography from '@sharedComponents/Typography';

import rowStyle from '@sharedComponents/Row/style.module.css';

const EMPTY = '—';

/**
 * Columns of the table of tests. The reference, the title and the pencil of a row open the page of the test
 * (`onOpen`); `sortBy` and `sortIcon` mark the sorted column; `languagesOptions` (each language id labelled by its
 * code) and `locale` name the language of a test.
 */
export const getTestColumns = (t, { onOpen = () => {}, sortBy = '', sortIcon, languagesOptions = [], locale = 'en' } = {}) => {
    const activeSortKey = sortBy.replace(/^-/, '');
    const iconFor = (key) => (activeSortKey === key ? sortIcon : SORT_ICON_NONE);

    return [
        {
            key: 'reference',
            label: t('Reference'),
            icon: iconFor('reference'),
            pinned: true,
            tdClassName: rowStyle.td,
            render: (test) => (
                <Action action={() => onOpen(test)} tabIndex={0} role="link">
                    <Typography span fontWeight="medium">
                        {test.reference}
                    </Typography>
                </Action>
            ),
        },
        {
            key: 'title',
            label: t('Title'),
            icon: iconFor('title'),
            pinned: true,
            // Takes the leftover width; the other columns shrink to their bounded content.
            fill: true,
            tdClassName: rowStyle.td,
            render: (test) => {
                const title = testTitle(test);
                return (
                    <Action action={() => onOpen(test)} tabIndex={0} role="link">
                        <Typography span color={title ? undefined : 'neutral-tertiary'}>
                            {title || t('Missing title')}
                        </Typography>
                    </Action>
                );
            },
        },
        {
            key: 'level',
            label: t('Level'),
            icon: iconFor('level'),
            tdClassName: rowStyle.td,
            render: (test) => test.level?.name ?? EMPTY,
        },
        {
            key: 'language',
            label: t('Language'),
            tdClassName: rowStyle.td,
            render: (test) => languageLabel(languagesOptions, test.languageId, locale) ?? EMPTY,
        },
        {
            key: 'status',
            label: t('Status'),
            icon: iconFor('status'),
            tdClassName: rowStyle.td,
            render: (test) => <TestStatusBadge status={test.status} />,
        },
        {
            key: 'collectionsCount',
            label: t('Collections'),
            icon: iconFor('collectionsCount'),
            tdClassName: rowStyle.td,
            render: (test) => test.collectionsCount,
        },
        {
            key: 'actions',
            ariaLabel: t('Actions'),
            pinned: true,
            tdClassName: rowStyle.tdActions,
            render: (test) => <Button ghost icon={faPenToSquare} title={t('Edit')} action={() => onOpen(test)} />,
        },
    ];
};
